import { useEffect } from "react";

import { getProfile } from "../../../api/profile/getProfile";
import { useAuth } from "../../../app/providers/AuthProvider";
import { getAccountScopedStoresResetVersion } from "../../../app/auth/sessionStoreReset";
import useAccountSettingsStore from "../store/useAccountSettingsStore";
import {
  hasCurrentAccountShellContract,
  markCurrentAccountShellContract,
} from "../utils/accountShellContract";
import { syncAccountProfileToShell } from "../utils/accountShellState";

export function useAccountShellHydration({ enabled = true } = {}) {
  const { updateAuthenticatedUser, user } = useAuth();
  const accountState = useAccountSettingsStore((state) => state.accountState);
  const setAccountState = useAccountSettingsStore((state) => state.setAccountState);

  useEffect(() => {
    const stateAccountId =
      accountState.accountId ||
      accountState.profile?.account_id ||
      accountState.profile?.id ||
      "";
    const stateMatchesUser = !stateAccountId || stateAccountId === user?.accountId;
    const loadedCurrentAccount = accountState.loaded && stateMatchesUser;
    const loadingCurrentAccount = accountState.loading && stateMatchesUser;
    const errorCurrentAccount = accountState.error && stateMatchesUser;
    const userHasShellSummary = Boolean(user?.accountName || user?.accountLogoUrl);
    const userHasPermissions = Boolean(
      user?.permissionsLoaded ||
      user?.isAccountOwner ||
      user?.is_account_owner ||
      user?.permissions
    );
    const needsContractRefresh =
      user?.accountId && !hasCurrentAccountShellContract(user.accountId);

    if (!enabled || !user?.accountId) {
      return undefined;
    }

    if (loadingCurrentAccount || errorCurrentAccount) {
      return undefined;
    }

    if (
      !needsContractRefresh &&
      ((userHasShellSummary && userHasPermissions) || loadedCurrentAccount)
    ) {
      return undefined;
    }

    let cancelled = false;
    const resetVersion = getAccountScopedStoresResetVersion();

    setAccountState((current) => ({
      ...current,
      accountId: user.accountId,
      error: "",
      loading: true,
    }));

    getProfile()
      .then((profile) => {
        if (cancelled || resetVersion !== getAccountScopedStoresResetVersion()) {
          return;
        }

        syncAccountProfileToShell({
          profile,
          setAccountState,
          updateAuthenticatedUser,
          user,
        });
        markCurrentAccountShellContract(user.accountId);
      })
      .catch((error) => {
        if (cancelled || resetVersion !== getAccountScopedStoresResetVersion()) {
          return;
        }

        setAccountState((current) => ({
          ...current,
          error: error?.message || "Não foi possível carregar as informações da conta.",
          loaded: current.loaded,
          loading: false,
        }));
      });

    return () => {
      cancelled = true;
    };
  }, [
    accountState.loaded,
    accountState.error,
    accountState.loading,
    accountState.accountId,
    accountState.profile?.account_id,
    accountState.profile?.id,
    enabled,
    setAccountState,
    updateAuthenticatedUser,
    user,
    user?.accountLogoUrl,
    user?.accountName,
    user?.isAccountOwner,
    user?.is_account_owner,
    user?.permissions,
    user?.permissionsLoaded,
  ]);
}
